import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { hasRouterFlag, pickListSearch } from './listRouting';

export interface ListViewRoutes {
    listKeys: readonly string[];
    fromListFlag: string;
    listPath: (listSearch: string) => string;
    viewPath: (patientId: string, listSearch: string) => string;
}

// A patient view carries its list's filters in its own URL: they pick the neighbours and rebuild the list on Back.
export function useListViewNavigation(routes: ListViewRoutes) {
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const listSearch = useMemo(
        () => pickListSearch(searchParams, routes.listKeys),
        [searchParams, routes.listKeys]
    );
    const routerState: unknown = location.state;
    // Prev/Next replace the entry and keep this flag, so the entry below is still the list.
    const cameFromList = hasRouterFlag(routerState, routes.fromListFlag);
    const listPath = routes.listPath(listSearch);

    const backToList = useCallback(() => {
        if (cameFromList) navigate(-1);
        else navigate(listPath);
    }, [cameFromList, listPath, navigate]);

    const goToPatient = useCallback(
        (patientId: string) =>
            navigate(routes.viewPath(patientId, listSearch), {
                replace: true,
                state: routerState
            }),
        [routes, listSearch, navigate, routerState]
    );

    return { searchParams, listPath, backToList, goToPatient };
}
